import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState, ActionMenu, FulfillmentSearchModal, CreateShipmentModal, SupplierRFQModal, SupplierQuotesModal } from '../../components/shared';
import { partOrders, parts, serialItems, purchaseOrders, suppliers, warehouses, partOrderMessages, supplierQuotes } from '../../services/dataClient';import ContentCard from "../../components/shared/ContentCard";

const TABS = [
{ label: 'All', value: 'All' },
{ label: 'Open', value: 'Open' },
{ label: 'Awaiting Quote', value: 'Awaiting Quote' },
{ label: 'Fulfilled', value: 'Fulfilled' },
{ label: 'Shipped', value: 'Shipped' },
{ label: 'Completed', value: 'Completed' }];

// ─── Origin abbreviation tag ──────────────────────────────────────────────────
// Shown inline at the end of the location/vendor name.
// FSL → teal, Supplier → orange, Warehouse/OnSite → grey-blue
const ORIGIN_ABBR = { FSL: 'FSL', Warehouse: 'WH', Supplier: 'SUP', OnSite: 'OS' };

function OriginTag({ method }) {
  if (!method) return null;
  const isFsl = method === 'FSL';
  const isSupplier = method === 'Supplier';
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '5px',
        py: '1px',
        borderRadius: '3px',
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.4,
        flexShrink: 0,
        backgroundColor: isFsl ? '#E0F2F1' : isSupplier ? '#FFF3E0' : '#ECEFF1',
        color: isFsl ? '#00695C' : isSupplier ? '#E65100' : '#455A64'
      }}>
      
      {ORIGIN_ABBR[method] ?? method}
    </Box>);

}

// ─── Chat helpers ─────────────────────────────────────────────────────────────
function resolveChatRecipient(order) {
  if (!order || order.status === 'Open' || !order.fulfillmentMethod) return null;
  if (order.fulfillmentMethod === 'Supplier') {
    const po = purchaseOrders.find((p) => p.id === order.linkedPurchaseOrderId);
    if (!po) return null;
    const sup = suppliers.find((s) => s.name === po.vendorName);
    return sup ? { name: sup.contactName, email: sup.contactEmail } : null;
  }
  const wh = warehouses.find((w) => w.name === order.locationName);
  return wh ? { name: wh.contactName, email: wh.contactEmail } : null;
}

function ChatBubbleIcon({ messages, orderStatus, onClick }) {
  if (orderStatus === 'Open') return null;
  const last = messages[messages.length - 1];
  // grey = no messages, blue = contact replied last, teal = coordinator sent last
  const color = messages.length === 0 ?
  'var(--grey-300)' :
  last.sender === 'contact' ? '#1565C0' : '#009688';
  const tooltip = messages.length === 0 ?
  'No messages yet — click to chat' :
  `${last.senderName}: ${last.text.slice(0, 60)}${last.text.length > 60 ? '…' : ''}`;
  return (
    <Tooltip title={tooltip} placement="top" arrow enterDelay={200}>
      <ForumIcon
        onClick={onClick}
        sx={{ fontSize: 17, color, display: 'block', cursor: 'pointer', '&:hover': { opacity: 0.75 } }} />
      
    </Tooltip>);

}

function ModalChatBody({ messages, recipient, onSend }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || !recipient) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {e.preventDefault();handleSend();}
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scroll area */}
      <Box
        ref={scrollRef}
        sx={{ flex: 1,
          overflowY: 'auto',
          px: 2, py: 2,
          display: 'flex', flexDirection: 'column', gap: '12px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' }
        }}>
        
        {recipient && messages.length === 0 &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center' }}>
              No messages yet. Start the conversation.
            </Typography>
          </Box>
        }
        {!recipient &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              Fulfill the order to enable messaging.
            </Typography>
          </Box>
        }
        {messages.map((msg) => {
          const isMe = msg.sender === 'coordinator';
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ maxWidth: '80%', px: '12px', py: '8px',
                borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                backgroundColor: isMe ? '#009688' : 'var(--grey-100)',
                color: isMe ? '#fff' : 'var(--grey-900)'
              }}>
                <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {msg.text}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>
                {fmtTime(msg.sentAt)}
              </Typography>
            </Box>);

        })}
      </Box>

      {/* Input row */}
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton
          size="small" disabled={!recipient}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' }, '&.Mui-disabled': { color: 'var(--grey-200)' } }}>
          
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField
          size="small" placeholder="Type a message…" multiline maxRows={3} fullWidth
          disabled={!recipient} value={inputText}
          onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', borderRadius: 0, '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } },
            '& .MuiInputBase-input': { py: '10px', px: '4px' }
          }} />
        
        <IconButton
          size="small" disabled={!inputText.trim() || !recipient} onClick={handleSend}
          sx={{ flexShrink: 0, mr: '10px', backgroundColor: '#009688', color: '#fff', borderRadius: '8px', width: 32, height: 32, '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' } }}>
          
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Box>);

}

// Enrich orders with part description
const enriched = partOrders.map((o) => ({
  ...o,
  partDescription: parts.find((p) => p.partNumber === o.partNumber)?.description ?? '—'
}));

// Break at the last space within `max` chars, or hard-cut if no space
function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

export default function PartOrdersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'createdDate', direction: 'desc' });

  // Local status overrides: { [ticketId]: status }
  const [overrides, setOverrides] = useState({});
  // Local fulfillment overrides: { [ticketId]: { fulfillmentMethod, locationName? } }
  const [fulfillmentOverrides, setFulfillmentOverrides] = useState({});
  // Local shipment overrides: { [ticketId]: { shipmentId, shipmentNumber } }
  const [shipmentOverrides, setShipmentOverrides] = useState({});

  // Fulfillment search modal
  const [searchRow, setSearchRow] = useState(null);
  // Supplier RFQ modal
  const [rfqRow, setRfqRow] = useState(null);
  const [quotesRow, setQuotesRow] = useState(null);
  // Supplier quotes map: { [partOrderId]: quote[] } — state value is write-only;
  // setter triggers re-render when new quotes arrive.
  // eslint-disable-next-line no-unused-vars
  const [quotesMap, setQuotesMap] = useState(() => {
    const map = {};
    supplierQuotes.forEach((q) => {
      if (!map[q.partOrderId]) map[q.partOrderId] = [];
      map[q.partOrderId].push(q);
    });
    return map;
  });
  // Create Shipment modal
  const [createShipmentRow, setCreateShipmentRow] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');

  // Chat modal state
  const [chatRow, setChatRow] = useState(null);
  const [messagesMap, setMessagesMap] = useState(() => {
    const map = {};
    partOrderMessages.forEach((m) => {
      if (!map[m.partOrderId]) map[m.partOrderId] = [];
      map[m.partOrderId].push(m);
    });
    return map;
  });

  const chatRecipient = chatRow ?
  resolveChatRecipient({ ...chatRow, ...(fulfillmentOverrides[chatRow.id] ?? {}) }) :
  null;
  const chatMessages = chatRow ? messagesMap[chatRow.id] ?? [] : [];

  const handleChatSend = (text) => {
    if (!chatRow) return;
    const msg = { id: `msg-local-${Date.now()}`, partOrderId: chatRow.id, sender: 'coordinator', senderName: 'You', text, sentAt: new Date().toISOString() };
    setMessagesMap((prev) => ({ ...prev, [chatRow.id]: [...(prev[chatRow.id] ?? []), msg] }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectStock = (stockLevel) => {
    if (!searchRow) return;
    // Auto-assign the first Available serial at this location (null for non-serialized parts)
    const assignedSerial = serialItems.find(
      (si) => si.partNumber === searchRow.partNumber &&
      si.locationName === stockLevel.locationName &&
      si.status === 'Available'
    ) ?? null;
    if (assignedSerial) {
      assignedSerial.status = 'Reserved';
      assignedSerial.ticketNumber = searchRow.ticketNumber;
    }
    searchRow.serialNumber = assignedSerial?.serialNumber ?? null;
    setFulfillmentOverrides((prev) => ({
      ...prev,
      [searchRow.id]: { fulfillmentMethod: stockLevel.locationType, locationName: stockLevel.locationName }
    }));
    setOverrides((prev) => ({ ...prev, [searchRow.id]: 'Fulfilled' }));
    setSearchRow(null);
    setBannerMsg(`Fulfillment source updated to ${stockLevel.locationName}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleRFQSubmit = ({ supplierNames, notes }) => {
    if (!rfqRow) return;
    const today = dayjs().format('YYYY-MM-DD');
    const newQuotes = supplierNames.map((name, i) => ({
      id: `sqr-new-${Date.now()}-${i}`,
      partOrderId: rfqRow.id,
      ticketNumber: rfqRow.ticketNumber,
      partNumber: rfqRow.partNumber,
      sentDate: today,
      supplierName: name,
      supplierEmail: suppliers.find((s) => s.name === name)?.contactEmail ?? '',
      unitCost: null,
      quantityAvailable: null,
      leadTimeDays: null,
      responseDate: null,
      notes: notes || null,
      status: 'Pending'
    }));
    setQuotesMap((prev) => ({
      ...prev,
      [rfqRow.id]: [...(prev[rfqRow.id] ?? []), ...newQuotes]
    }));
    setOverrides((prev) => ({ ...prev, [rfqRow.id]: 'Awaiting Quote' }));
    const n = supplierNames.length;
    setBannerMsg(`Quote requests sent to ${n} supplier${n !== 1 ? 's' : ''} for ${rfqRow.ticketNumber}.`);
    setRfqRow(null);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const handleMarkAsUsedForRow = (row) => {
    setOverrides((prev) => ({ ...prev, [row.id]: 'Completed' }));
    setBannerMsg(`Part marked as used for ${row.ticketNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleCreateShipmentForRow = (newShipment) => {
    if (!createShipmentRow) return;
    const linked = { ...newShipment, partOrderId: createShipmentRow.id, ticketNumber: createShipmentRow.ticketNumber };
    setShipmentOverrides((prev) => ({
      ...prev,
      [createShipmentRow.id]: { shipmentId: linked.id, shipmentNumber: linked.shipmentNumber }
    }));
    setOverrides((prev) => ({ ...prev, [createShipmentRow.id]: 'Shipped' }));
    setCreateShipmentRow(null);
    setBannerMsg(`Shipment ${linked.shipmentNumber} created and linked to ${createShipmentRow.ticketNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched
    .map((o) => ({
      ...o,
      status: overrides[o.id] ?? o.status,
      ...(fulfillmentOverrides[o.id] ?? {}),
      ...(shipmentOverrides[o.id] ?? {})
    }))
    .filter((o) => activeTab === 'All' || o.status === activeTab)
    .filter((o) =>
    !q ||
    o.ticketNumber.toLowerCase().includes(q) ||
    o.partNumber.toLowerCase().includes(q) ||
    o.customerName.toLowerCase().includes(q) ||
    (o.resellerName ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const va = a[sortConfig.field] ?? '';
      const vb = b[sortConfig.field] ?? '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [activeTab, search, sortConfig, overrides, fulfillmentOverrides, shipmentOverrides]);

  const columns = [
  {
    field: 'ticketNumber',
    headerName: 'Ticket #',
    sortable: true,
    renderCell: (row) =>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Typography
        onClick={(e) => {e.stopPropagation();navigate(`/orders/parts/${row.id}`);}}
        sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
        
            {row.ticketNumber}
          </Typography>
          {row.linkedPurchaseOrderNumber &&
      <Typography
        onClick={(e) => {e.stopPropagation();navigate(`/accounting/purchase-orders/${row.linkedPurchaseOrderId}`);}}
        sx={{ fontSize: '0.7rem', color: 'var(--grey-400)', cursor: 'pointer', '&:hover': { color: '#009688', textDecoration: 'underline' } }}>
        
              {row.linkedPurchaseOrderNumber}
            </Typography>
      }
        </Box>

  },
  {
    field: 'partNumber',
    headerName: 'Part #',
    sortable: true,
    renderCell: (row) =>
    <Tooltip title={row.partDescription} placement="top" arrow enterDelay={200}>
          <Typography sx={{ fontSize: '0.875rem', cursor: 'default' }}>
            {row.partNumber}
          </Typography>
        </Tooltip>

  },
  {
    field: 'resellerName',
    headerName: 'Reseller',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.resellerName);
      return (
        <Tooltip title={truncated ? row.resellerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: row.resellerName ? 'var(--grey-800)' : 'var(--grey-400)' }}>
              {row.resellerName ? short : '—'}
            </Typography>
          </Tooltip>);

    }
  },
  {
    field: 'customerName',
    headerName: 'End-Customer',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.customerName);
      return (
        <Tooltip title={truncated ? row.customerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
              {short}
            </Typography>
          </Tooltip>);

    }
  },
  { field: 'createdDate', headerName: 'Created', sortable: true },
  {
    field: 'sla',
    headerName: 'SLA',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem' }}>{row.sla}</Typography>

  },
  {
    field: 'fulfillmentMethod',
    headerName: 'Origin',
    sortable: true,
    renderCell: (row) => {
      if (row.status === 'Open' || !row.fulfillmentMethod) {
        return <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>;
      }
      let sourceName = row.locationName;
      if (row.fulfillmentMethod === 'Supplier') {
        const po = purchaseOrders.find((p) => p.id === row.linkedPurchaseOrderId);
        sourceName = po?.vendorName ?? null;
      }
      const { short, truncated } = truncate(sourceName);
      return (
        <Tooltip title={truncated ? sourceName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                {short ?? '—'}
              </Typography>
              <OriginTag method={row.fulfillmentMethod} />
            </Box>
          </Tooltip>);

    }
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  {
    field: '_notif',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
          <ChatBubbleIcon
        messages={messagesMap[row.id] ?? []}
        orderStatus={row.status}
        onClick={() => setChatRow(row)} />
      
        </Box>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) => {
      const partRec = parts.find((p) => p.partNumber === row.partNumber);
      const actions = [
      { label: 'View Detail', onClick: () => navigate(`/orders/parts/${row.id}`) },
      ...(row.status === 'Open' ?
      [
      { label: 'Fulfill Order', onClick: () => setSearchRow(row) },
      { label: 'Request Supplier Quotes', onClick: () => setRfqRow(row) }] :

      []),
      ...(row.status === 'Awaiting Quote' ?
      [{ label: 'View Quotes', onClick: () => setQuotesRow(row) }] :
      []),
      ...(row.status === 'Fulfilled' && row.fulfillmentMethod !== 'OnSite' ?
      [
      { label: 'Change Fulfillment Source', onClick: () => setSearchRow(row) },
      { label: 'Create Shipment', onClick: () => setCreateShipmentRow(row) }] :

      []),
      ...(row.status === 'Fulfilled' && row.fulfillmentMethod === 'OnSite' ?
      [{ label: 'Mark as Used', onClick: () => handleMarkAsUsedForRow(row) }] :
      []),
      ...((row.status === 'Shipped' || row.status === 'Completed') && row.shipmentId ?
      [{ label: 'View Shipment', onClick: () => navigate(`/shipments/dispatch/${row.shipmentId}`) }] :
      []),
      ...(partRec ?
      [{ label: 'View in Inventory', onClick: () => navigate(`/inventory/search/${partRec.id}`) }] :
      [])];

      return (
        <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>);

    }
  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMsg}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Part Orders"
        crumbs={[
        { label: 'Orders' },
        { label: 'Part Orders' }]
        } />

      {/* White card wrapping tabs + search + table */}
      <ContentCard
        sx={{ p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
        
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by Ticket #, Part #, Customer, or Reseller…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment:
            <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
              </InputAdornment>

          }}
          sx={{ minWidth: 400, maxWidth: 400, backgroundColor: 'var(--paper)', borderRadius: 1 }} />

        {/* Table */}
        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState
            title="No orders found"
            description="Try adjusting your filter or search query." /> :

          <DataTable
            columns={columns}
            data={filtered}
            onRowClick={(row) => navigate(`/orders/parts/${row.id}`)}
            sortConfig={sortConfig}
            onSort={handleSort} />

          }
        </Box>
      </ContentCard>

      {/* Fulfillment search modal */}
      <FulfillmentSearchModal
        open={Boolean(searchRow)}
        order={searchRow}
        onClose={() => setSearchRow(null)}
        onSelectStock={handleSelectStock}
        onRequestSupplierQuote={() => {setRfqRow(searchRow);setSearchRow(null);}} />

      {/* Supplier RFQ modal */}
      <SupplierRFQModal
        open={Boolean(rfqRow)}
        order={rfqRow}
        onClose={() => setRfqRow(null)}
        onSubmit={handleRFQSubmit} />

      <SupplierQuotesModal
        open={Boolean(quotesRow)}
        order={quotesRow}
        onClose={() => setQuotesRow(null)}
        onStatusChange={(newStatus) => {
          if (quotesRow) setOverrides((prev) => ({ ...prev, [quotesRow.id]: newStatus }));
          setQuotesRow(null);
        }} />

      {/* Chat modal */}
      <Dialog
        open={Boolean(chatRow)}
        onClose={(e, reason) => {if (reason !== 'backdropClick') setChatRow(null);}}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: 500, display: 'flex', flexDirection: 'column' } }}>
        
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
              Messages — {chatRow?.ticketNumber}
            </Typography>
            {chatRecipient ?
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                {chatRecipient.name} · {chatRecipient.email}
              </Typography> :

            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', mt: '2px', fontStyle: 'italic' }}>
                No fulfillment source assigned
              </Typography>
            }
          </Box>
          <IconButton size="small" onClick={() => setChatRow(null)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', px: 0, pb: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ModalChatBody messages={chatMessages} recipient={chatRecipient} onSend={handleChatSend} />
        </DialogContent>
      </Dialog>

      {/* Create Shipment modal — pre-filled from the Fulfilled row */}
      <CreateShipmentModal
        open={Boolean(createShipmentRow)}
        onClose={() => setCreateShipmentRow(null)}
        onSubmit={handleCreateShipmentForRow}
        prefill={createShipmentRow ? (() => {
          const addr = createShipmentRow.customerAddress ?? '';
          const parts = addr.split(', ');
          const stateZip = (parts[parts.length - 1] ?? '').split(' ');
          return {
            ticketNumber: createShipmentRow.ticketNumber,
            originType: createShipmentRow.fulfillmentMethod === 'Supplier' ?
            'Supplier' :
            createShipmentRow.fulfillmentMethod ?? '',
            originLocation: createShipmentRow.locationName ?? '',
            customerName: createShipmentRow.customerName,
            customerAddress: parts.length >= 3 ? parts.slice(0, parts.length - 2).join(', ') : addr,
            customerCity: parts.length >= 3 ? parts[parts.length - 2] : '',
            customerState: parts.length >= 3 ? stateZip[0] ?? '' : '',
            customerZip: parts.length >= 3 ? stateZip[1] ?? '' : ''
          };
        })() : undefined} />
      
    </Box>);

}